import { useParams } from 'react-router-dom';
import { School, GraduationCap, CheckCircle2, AlertTriangle, Activity, Clock, ClipboardList, Users, BarChart3 } from 'lucide-react';
import { KPICard, LoadingSpinner, ErrorPanel, FeedTable, StatsPanel, ResponsiveStyles } from '../../components/dashboard/DashboardShared';
import { useQuery } from '@tanstack/react-query';
import '../../components/ui/datatable.css';

const API = import.meta.env.VITE_API_URL || '/api/v1';

export function DashboardEscolaConsolidado() {
  const { escolaId } = useParams<{ escolaId: string }>();
  const token = localStorage.getItem('ac_token');

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard-escola', escolaId],
    queryFn: async () => {
      const res = await fetch(`${API}/escolas/${escolaId}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      return res.json();
    },
    enabled: !!escolaId,
    refetchInterval: 15000,
  });

  if (isLoading) return <LoadingSpinner label="Carregando Dashboard da Escola..." />;
  if (error) return <ErrorPanel />;

  const c = data?.cards || {};
  const escola = data?.escola || {};
  const notif = data?.analitico?.resumoNotificacoes || {};
  const aderencia = data?.analitico?.aderenciaChamadas || {};
  const censo = data?.censo || {};
  const equipe = data?.equipe || {};

  return (
    <div className="admin-page animate-fadeIn">
      <div className="page-header">
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <School size={28} style={{ color: '#059669' }} />
            {escola.nome || 'Dashboard da Escola'}
          </h1>
          <p>Turno: {data?.turnoOperacional || 'Auto'} · {new Date().toLocaleDateString('pt-BR')}</p>
        </div>
        {/* Seletor de turno */}
        {(data?.turnosDisponiveis || []).length > 0 && (
          <div style={{ display: 'flex', gap: 6 }}>
            {data.turnosDisponiveis.map((t: any) => (
              <span key={t.id} style={{
                padding: '4px 12px', borderRadius: 8, fontSize: '0.75rem', fontWeight: 600,
                background: t.nome === data.turnoOperacional ? '#059669' : 'var(--bg-main)',
                color: t.nome === data.turnoOperacional ? 'white' : 'var(--text-muted)',
              }}>{t.nome}</span>
            ))}
          </div>
        )}
      </div>

      {/* KPIs principais */}
      <div className="kpi-grid kpi-grid-4">
        <KPICard icon={GraduationCap} label="Previstos" value={c.totalPrevistos?.toLocaleString('pt-BR') || 0} color="#6366f1" />
        <KPICard icon={CheckCircle2} label="Presentes" value={c.totalPresentes?.toLocaleString('pt-BR') || 0} sub={`${c.totalAtrasados || 0} atrasados`} color="#16a34a" />
        <KPICard icon={AlertTriangle} label="Ausentes" value={c.totalAusentes?.toLocaleString('pt-BR') || 0} color="#dc2626" />
        <KPICard icon={AlertTriangle} label="Alertas" value={c.alertasAtivos || 0} sub={`${c.totalInconsistencias || 0} inconsist.`} color="#ef4444" />
      </div>

      <div className="kpi-grid kpi-grid-4" style={{ marginTop: 16 }}>
        <KPICard icon={Activity} label="Entradas" value={c.totalEntraram?.toLocaleString('pt-BR') || 0} color="#2563eb" />
        <KPICard icon={Activity} label="Saídas" value={c.totalSaidas?.toLocaleString('pt-BR') || 0} color="#6b7280" />
        <KPICard icon={ClipboardList} label="Aderência Chamada" value={`${aderencia.taxa || 0}%`} sub={`${aderencia.comChamada || 0}/${aderencia.totalTurmas || 0} turmas`} color="#7c3aed" />
        <KPICard icon={Users} label="Equipe" value={`${equipe.presentes || 0}/${equipe.totalPrevistos || 0}`} sub={`${equipe.ausentes || 0} ausentes`} color="#0891b2" />
      </div>

      {/* Resumo por Turma + Alertas */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 24 }}>
        <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', padding: 20 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <BarChart3 size={18} style={{ color: '#6366f1' }} /> Presença por Turma
          </h3>
          {(data?.analitico?.resumoTurmas || []).length === 0 ? (
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>Nenhuma turma</p>
          ) : (
            <div style={{ maxHeight: 340, overflowY: 'auto' }}>
              <table className="dt-table" style={{ width: '100%', fontSize: '0.82rem' }}>
                <thead><tr><th>Turma</th><th>Série</th><th>Matric.</th><th>Pres.</th><th>Aus.</th><th>Tax.</th></tr></thead>
                <tbody>
                  {(data.analitico.resumoTurmas || []).map((r: any) => {
                    const taxa = r.matriculados > 0 ? Math.round(((r.presentes + r.atrasados) / r.matriculados) * 100) : 0;
                    return (
                      <tr key={r.turma.id}>
                        <td style={{ fontWeight: 600 }}>{r.turma.nome}</td>
                        <td>{r.turma.serie}</td>
                        <td>{r.matriculados}</td>
                        <td style={{ color: '#16a34a' }}>{r.presentes}</td>
                        <td style={{ color: r.ausentes > 0 ? '#dc2626' : undefined }}>{r.ausentes}</td>
                        <td><strong style={{ color: taxa >= 80 ? '#16a34a' : taxa >= 60 ? '#d97706' : '#dc2626' }}>{taxa}%</strong></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', padding: 20 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={18} style={{ color: '#dc2626' }} /> Incidentes Ativos ({data?.alertas?.incidentesAtivos?.length || 0})
          </h3>
          {(data?.alertas?.incidentesAtivos || []).length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>
              <CheckCircle2 size={28} style={{ margin: '0 auto 8px', color: '#16a34a' }} />
              <p>Nenhum incidente ativo</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflowY: 'auto' }}>
              {(data.alertas.incidentesAtivos || []).map((a: any) => (
                <div key={a.id} style={{ padding: '10px 14px', background: a.severidade === 'CRITICA' ? '#fef2f2' : '#fffbeb', borderRadius: 10, borderLeft: `3px solid ${a.severidade === 'CRITICA' ? '#dc2626' : '#d97706'}` }}>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{a.tipoIncidente?.replace(/_/g, ' ')}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                    {a.aluno?.nome || 'Sistema'} · {a.turma?.nome || ''} · {new Date(a.dataHoraReferencia).toLocaleString('pt-BR')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Censo + Notificações */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 24 }}>
        <StatsPanel title="Censo Escolar" icon={School} iconColor="#059669" items={[
          { label: 'Alunos', value: censo.alunos || 0 },
          { label: 'Turmas', value: censo.turmas || 0 },
          { label: 'Profissionais', value: censo.profissionais || 0 },
          { label: 'Responsáveis', value: censo.responsaveis || 0 },
        ]} />
        <StatsPanel title="Notificações Hoje" icon={Clock} iconColor="#6366f1" items={[
          { label: 'Total Disparadas', value: notif.total || 0 },
          { label: 'Entregues', value: notif.entregues || notif.enviadas || 0, color: '#2563eb' },
          { label: 'Lidas', value: notif.lidas || 0, color: '#16a34a' },
          { label: 'Falhas', value: notif.falhas || 0, color: '#dc2626' },
          { label: 'Taxa Leitura', value: `${notif.taxaLeitura || 0}%`, color: '#7c3aed' },
        ]} />
      </div>

      {/* Feed */}
      <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', padding: 20, marginTop: 24 }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Activity size={18} style={{ color: '#2563eb' }} /> Feed de Eventos
        </h3>
        <FeedTable feed={data?.operacao?.feed || []} />
      </div>

      <ResponsiveStyles />
    </div>
  );
}
